import { FileTextOutlined } from '@ant-design/icons';
import { Empty, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { EmptyDataContainerStyled } from 'components/common/EmptyData';
import IconComponent from 'components/common/IconComponent';
import LinkedDocument from 'components/common/LinkedDocument';
import Spinner from 'components/Spinner';
import Table from 'components/Table';
import { DataEntity, MobileTableType } from 'components/Table/types';
import React, { useMemo, useState } from 'react';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useIntl } from 'react-intl';
// import APIService from 'services/apiUtils';
import { GeneralFunction } from 'types/common';
import { IFile } from 'types/file';
import { formatFileSize } from 'utils';
import IntlMessages, { formatMessage } from 'utils/IntlMessages';

import { TextOnlyButtonStyled } from '../../styled';

interface IDocumentList {
  onEditFileClick?: GeneralFunction;
  onDeleteFileClick?: GeneralFunction;
  files?: IFile[];
}

const DocumentList: React.FC<IDocumentList> = ({ onDeleteFileClick, files }) => {
  const [selectedFile, setSelectedFile] = useState<IFile>();
  const [deleteLoading, setDeleteLoading] = useState<boolean>();

  const filesLength = files?.length || 0;
  const intl = useIntl();

  const deSelectFile = () => {
    setSelectedFile(undefined);
  };

  const onDeleteFileConfirm = () => {
    setDeleteLoading(true);
    onDeleteFileClick?.(selectedFile, () => {
      setSelectedFile(undefined);
      setDeleteLoading(false);
    });
  };

  const documentColumns: ColumnsType<any> = [
    {
      title: 'File',
      dataIndex: 'file',
      render: (file: IFile) => (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div style={{ marginRight: 10, display: 'flex', alignItems: 'center' }}>
            <FileTextOutlined style={{ fontSize: 30 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <LinkedDocument {...file} size={undefined} />
          </div>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
    },
    {
      title: 'Type',
      dataIndex: 'type',
    },
    {
      title: 'Size',
      dataIndex: 'size',
    },
    {
      title: '',
      dataIndex: 'delete',
      width: '80px',
      render: (file: IFile) => (
        <TextOnlyButtonStyled
          className='gx-ml-2'
          style={{ display: 'flex', flex: 1, justifyContent: 'center' }}
          onClick={() => {
            setSelectedFile(file);
          }}>
          <Tooltip title='Delete document'>
            <IconComponent color='#ff4747' name='trash' />
          </Tooltip>
        </TextOnlyButtonStyled>
      ),
    },
  ];

  const dataMapper = (file: IFile) => ({
    key: file.id,
    description: file.details?.description || 'No description',
    notes: file.details?.notes || '-',
    size: formatFileSize(file.size),
    delete: file,
    type: file.extension,
    file,
  });

  const dataSource = useMemo<DataEntity[] | undefined>(
    () => (dataMapper ? files?.map(dataMapper) : ((files as unknown) as DataEntity[] | undefined)),
    [dataMapper, files],
  );

  return (
    <>
      <SweetAlert
        show={!!selectedFile}
        error
        title={formatMessage(intl, 'appModule.deleteRecordTitle')}
        disabled={deleteLoading}
        confirmBtnStyle={{ width: 72 }}
        confirmBtnText={<span>{deleteLoading ? <Spinner /> : formatMessage(intl, 'appModule.delete')}</span>}
        onConfirm={onDeleteFileConfirm}
        onCancel={deSelectFile}
        showCancel
        reverseButtons>
        <IntlMessages id='appModule.deleteRecordBody' />
      </SweetAlert>
      {filesLength > 0 ? (
        <Table
          columns={documentColumns}
          dataSource={dataSource}
          mobileDesignType={MobileTableType.RESPONSIVE_TABLE}
          responsiveFullBordered
          widthThreshold={597}
          pagination={false}
          style={{ marginBottom: 50 }}
        />
      ) : (
        <EmptyDataContainerStyled className='empty-data'>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </EmptyDataContainerStyled>
      )}
    </>
  );
};

export default DocumentList;
